/* @flow */
import React from 'react';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';

import type { TranslatorProps } from 'react-i18next';

import type TypeActionDrawerStore from 'stores/ActionDrawerStore';

import Form from 'forms/_.extend';
import Loading from 'components/Loading/Loading';
import ActionDrawerHeader from 'views/ActionDrawer/components/ActionDrawerHeader/ActionDrawerHeader';

import ChangeAssetLocationForm from './ChangeAssetLocationForm/ChangeAssetLocationForm';
import ChangeAssetLocationSuccess from './ChangeAssetLocationSuccess/ChangeAssetLocationSuccess';
import ChangeAssetLocationError from './ChangeAssetLocationError/ChangeAssetLocationError';

import styles from '../../../ActionDrawer.module.scss';

type TypeProps = {|
  actionDrawerStore: TypeActionDrawerStore,
|} & TranslatorProps;

@translate()
@inject('actionDrawerStore')
@observer
export default class ChangeAssetLocation extends React.Component<*> {
  get value() {
    const asset: Object = this.props.formData;
    const location: Object = asset.location;

    return location.workspace && location.workspace.id
      ? location.workspace.id
      : location.room && location.room.id
      ? location.room.id
      : location.building && location.building.id
      ? location.building.id
      : location.site && location.site.id
      ? location.site.id
      : location.site && location.site.country_id
      ? location.site.country_id
      : location.company && location.company.id
      ? location.company.id
      : null;
  }

  createForm = () => {
    const { t, actionDrawerStore }: TypeProps = this.props;

    const asset: Object = this.props.formData;

    const fields: Array<Object> = [
      {
        name: 'changeLocation',
        label: t('general:actions.change_location_to'),
        fields: [
          {
            name: 'location',
            label: t('general:location'),
            rules: 'required|string',
            value: this.value,
            extra: {
              hierarchy: [
                {
                  name: 'company',
                  type: 'company',
                  placeholder: t('general:company'),
                  value: asset.location.company.id
                    ? {
                        id: asset.location.company.id,
                        name: asset.location.company.name,
                      }
                    : null,
                },
                {
                  name: 'country',
                  type: 'country',
                  placeholder: t('general:country'),
                  value: asset.location.site.country_id
                    ? {
                        id: asset.location.site.country_id,
                        name: asset.location.site.country_name,
                      }
                    : null,
                },
                {
                  name: 'site',
                  type: 'location',
                  placeholder: t('general:locations.labels.site'),
                  value: asset.location.site.id
                    ? {
                        id: asset.location.site.id,
                        name: asset.location.site.name,
                      }
                    : null,
                },
                {
                  name: 'building',
                  type: 'location',
                  placeholder: t('general:locations.labels.building_transport'),
                  value: asset.location.building.id
                    ? {
                        id: asset.location.building.id,
                        name: asset.location.building.name,
                      }
                    : null,
                },
                {
                  name: 'room',
                  type: 'location',
                  placeholder: t('general:locations.labels.lab_room'),
                  value: asset.location.room.id
                    ? {
                        id: asset.location.room.id,
                        name: asset.location.room.name,
                      }
                    : null,
                },
                {
                  name: 'workspace',
                  type: 'location',
                  placeholder: t('general:locations.labels.system_workspace'),
                  value: asset.location.workspace.id
                    ? {
                        id: asset.location.workspace.id,
                        name: asset.location.workspace.name,
                      }
                    : null,
                },
              ],
            },
          },
        ],
      },
    ];

    if (asset.owned) {
      fields.push({
        name: 'moveAssets',
        label: t('general:actions.select_assets_to_move'),
        fields: [
          {
            name: 'assetsToMove',
            label: t('general:assets.to_move'),
            rules: 'required|array',
            value: [],
          },
        ],
      });
    }

    const hooks: Object = {
      onSuccess(data: Object) {
        const { location }: Object = data.values().changeLocation;

        let assets: Array<string> = [asset.id];

        if (asset.owned) {
          assets = data.values().moveAssets.assetsToMove;
        }

        actionDrawerStore.changeAssetLocation(asset.id, {
          location_id: location,
          asset_ids: assets,
        });
      },
    };

    return new Form(
      { fields },
      {
        hooks,
        name: 'changeAssetLocation',
      }
    );
  };

  render(): React.Node {
    const { t, actionDrawerStore }: TypeProps = this.props;

    const asset: Object = this.props.formData;

    const title: string = t('general:actions.change_location');

    if (actionDrawerStore.loading) {
      return <Loading />;
    }

    if (actionDrawerStore.error) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.error}`}>
          <ActionDrawerHeader title={title} />
          <ChangeAssetLocationError asset={asset} />
        </div>
      );
    }

    if (actionDrawerStore.success) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.success}`}>
          <ActionDrawerHeader title={title} />
          <ChangeAssetLocationSuccess response={actionDrawerStore.response} />
        </div>
      );
    }

    return (
      <div className={styles['action-drawer-form']}>
        <ActionDrawerHeader title={title} />
        <ChangeAssetLocationForm asset={asset} form={this.createForm()} />
      </div>
    );
  }
}
